import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  AlertCircle,
  BookOpen,
  Calendar,
  Clock,
  Loader2,
  MapPin,
  MessageSquare,
  PlayCircle,
  Sparkles,
  Star,
  Wallet,
  XCircle,
} from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { apiFetch, ApiError } from "@/lib/api-client";

interface PortalBookingDetails {
  id: string;
  serviceId: string;
  startAt: string;
  endAt: string;
  status: "confirmed" | "requested" | "completed" | "cancelled" | "late_cancelled" | "in_progress" | "no_show";
  locationMode: string;
  pricePaidCents: number | null;
}

interface JourneyStep {
  type: "booking" | "enrollment";
  id: string;
  serviceName: string;
  status: "completed" | "upcoming" | "in_progress" | "cancelled" | "no_show";
  date: string;
  details: PortalBookingDetails;
}

interface JourneyData {
  steps: JourneyStep[];
  tenantSlug: string | null;
  bookingEntryUrl: string | null;
  stats: {
    completedBookings: number;
    upcomingBookings: number;
    activeEnrollments: number;
    activeCourseEnrollments?: number;
    totalCourseEnrollments?: number;
    totalVisits: number;
  };
}

interface PortalCourseCatalogData {
  courses: Array<{
    enrollment: {
      status: string;
    };
    course: {
      slug: string;
      title: string;
      description: string | null;
    };
    progressSummary: {
      completedLessons: number;
      totalLessons: number;
      progressPercent: number;
    };
    nextLesson: {
      id: string;
      title: string;
      lessonType: string;
      moduleTitle: string;
    } | null;
  }>;
}

interface LoyaltyAccount {
  id: string;
  accountType: string;
  balance: number;
  currency: string;
  expiresAt: string | null;
}

interface LoyaltyData {
  tenantSlug: string | null;
  bookingEntryUrl: string | null;
  memberSince: string | null;
  completedBookings: number;
  totalSpentCents: number;
  totalCreditBalance: number;
  tier: {
    key: string;
    label: string;
    nextTierLabel: string | null;
    bookingsToNextTier: number;
  };
  accounts: LoyaltyAccount[];
}

interface Recommendation {
  type: "rebooking" | "package_upgrade" | "referral" | "new_service";
  title: string;
  description: string;
  action: string;
  metadata: Record<string, unknown>;
}

interface RecommendationData {
  recommendations: Recommendation[];
  bookingEntryUrl: string | null;
}

interface AvailabilitySlot {
  startTime: string;
  endTime: string;
  resourceId: string;
  resourceName: string;
}

interface AvailabilityData {
  service: {
    id: string;
    name: string;
  };
  slots: AvailabilitySlot[];
}

const STATUS_STYLES: Record<string, string> = {
  upcoming: "bg-emerald-500/10 text-emerald-700",
  in_progress: "bg-sky-500/10 text-sky-700",
  completed: "bg-slate-500/10 text-slate-700",
  cancelled: "bg-rose-500/10 text-rose-700",
  no_show: "bg-amber-500/10 text-amber-700",
};

const TIMELINE_COLORS = [
  "bg-sky-500",
  "bg-emerald-500",
  "bg-violet-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-cyan-500",
] as const;

function hashString(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
}

function formatMoney(cents: number, currency = "EUR") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatLocation(locationMode: string) {
  return locationMode.replaceAll("_", " ");
}

function getBookingEntryUrl(primary: string | null | undefined, fallback: string | null | undefined) {
  return primary ?? fallback ?? null;
}

function formatCourseProgress(course: PortalCourseCatalogData["courses"][number]) {
  const { completedLessons, totalLessons } = course.progressSummary;
  const lessonLabel = totalLessons === 1 ? "lesson" : "lessons";
  return `${completedLessons}/${totalLessons} ${lessonLabel}`;
}

export default function PortalDashboard() {
  const journey = useApi<JourneyData>("/v1/portal/journey");
  const loyalty = useApi<LoyaltyData>("/v1/portal/loyalty");
  const recommendations = useApi<RecommendationData>("/v1/portal/recommendations");

  const [feedback, setFeedback] = useState<string | null>(null);
  const [busyBookingId, setBusyBookingId] = useState<string | null>(null);
  const [rescheduleBooking, setRescheduleBooking] = useState<JourneyStep | null>(null);
  const [availability, setAvailability] = useState<AvailabilityData | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  const steps = journey.data?.steps ?? [];
  const upcoming = steps.filter((step) =>
    step.type === "booking" && (step.status === "upcoming" || step.status === "in_progress"),
  );

  const bookingEntryUrl = getBookingEntryUrl(
    journey.data?.bookingEntryUrl,
    loyalty.data?.bookingEntryUrl ?? recommendations.data?.bookingEntryUrl,
  );
  const activeCourseCount = journey.data?.stats.activeCourseEnrollments ?? journey.data?.stats.activeEnrollments ?? 0;
  const showCourseEntry = !journey.isLoading && activeCourseCount > 0;
  const courseCatalog = useApi<PortalCourseCatalogData>("/v1/portal/courses", {
    enabled: showCourseEntry,
  });
  const continueCourse = courseCatalog.data?.courses.find((course) => course.nextLesson) ?? courseCatalog.data?.courses[0] ?? null;

  const totalCreditsLabel = useMemo(() => {
    const primaryAccount = loyalty.data?.accounts[0];
    if (!primaryAccount) {
      return "No credits yet";
    }

    return `${primaryAccount.balance.toFixed(2)} ${primaryAccount.accountType.replaceAll("_", " ")}`;
  }, [loyalty.data?.accounts]);

  async function refreshPortal() {
    journey.refetch();
    loyalty.refetch();
    recommendations.refetch();
    courseCatalog.refetch();
  }

  async function handleCancelBooking(bookingId: string) {
    setBusyBookingId(bookingId);
    setFeedback(null);

    try {
      await apiFetch(`/crm/bookings/${bookingId}/cancel`, {
        method: "POST",
        body: JSON.stringify({
          reason: "Cancelled from client portal",
        }),
      });
      setFeedback("Booking cancelled.");
      await refreshPortal();
    } catch (error) {
      setFeedback(
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Unable to cancel booking.",
      );
    } finally {
      setBusyBookingId(null);
    }
  }

  async function openReschedule(booking: JourneyStep) {
    if (booking.type !== "booking") {
      return;
    }

    setRescheduleBooking(booking);
    setAvailability(null);
    setAvailabilityError(null);
    setAvailabilityLoading(true);
    setFeedback(null);

    try {
      const response = await apiFetch<{ data: AvailabilityData }>(`/crm/bookings/availability/${booking.details.serviceId}`);
      setAvailability(response.data);
    } catch (error) {
      setAvailabilityError(
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Unable to load reschedule options.",
      );
    } finally {
      setAvailabilityLoading(false);
    }
  }

  async function handleReschedule(slot: AvailabilitySlot) {
    if (!rescheduleBooking) {
      return;
    }

    setBusyBookingId(rescheduleBooking.id);
    setFeedback(null);

    try {
      await apiFetch(`/crm/bookings/${rescheduleBooking.id}/reschedule`, {
        method: "POST",
        body: JSON.stringify({
          startTime: slot.startTime,
          endTime: slot.endTime,
        }),
      });
      setFeedback("Booking rescheduled.");
      setRescheduleBooking(null);
      setAvailability(null);
      await refreshPortal();
    } catch (error) {
      setAvailabilityError(
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Unable to reschedule booking.",
      );
    } finally {
      setBusyBookingId(null);
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">Client portal</p>
          <h1 className="mt-2 text-3xl font-semibold text-[var(--color-foreground)]">Your care journey</h1>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Track upcoming visits, adjust bookings, review your progress, and keep an eye on available credits from one surface.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {showCourseEntry ? (
            <Link
              to="/portal/courses"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-2.5 text-sm font-medium text-white hover:opacity-90"
            >
              <BookOpen className="h-4 w-4" />
              Continue courses
            </Link>
          ) : null}
          {bookingEntryUrl ? (
            <a
              href={bookingEntryUrl}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--color-border)] px-4 py-2.5 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
            >
              <Calendar className="h-4 w-4" />
              Book next visit
            </a>
          ) : null}
        </div>
      </div>

      {feedback ? (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-foreground)]">
          {feedback}
        </div>
      ) : null}

      {journey.error ? (
        <div className="rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-5 text-sm text-[var(--color-error)]">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>Failed to load your portal journey. {journey.error}</span>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-4">
        {showCourseEntry ? (
          <Link
            to="/portal/courses"
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 transition hover:border-[var(--color-primary)]/40 hover:shadow-sm"
          >
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Active courses</p>
            <p className="mt-3 text-3xl font-semibold text-[var(--color-foreground)]">{activeCourseCount}</p>
            <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">Open your mounted course catalog.</p>
          </Link>
        ) : null}
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Upcoming</p>
          <p className="mt-3 text-3xl font-semibold text-[var(--color-foreground)]">
            {journey.isLoading ? "..." : journey.data?.stats.upcomingBookings ?? 0}
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Completed visits</p>
          <p className="mt-3 text-3xl font-semibold text-[var(--color-foreground)]">
            {journey.isLoading ? "..." : journey.data?.stats.completedBookings ?? 0}
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Available credits</p>
          <p className="mt-3 text-lg font-semibold text-[var(--color-foreground)]">
            {loyalty.isLoading ? "..." : totalCreditsLabel}
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Lifetime spend</p>
          <p className="mt-3 text-lg font-semibold text-[var(--color-foreground)]">
            {loyalty.isLoading ? "..." : formatMoney(loyalty.data?.totalSpentCents ?? 0)}
          </p>
        </div>
      </div>

      {showCourseEntry && continueCourse ? (
        <section className="rounded-[28px] border border-[var(--color-primary)]/25 bg-[var(--color-background)] p-6 shadow-sm">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                Continue learning
              </p>
              <h2 className="mt-2 text-xl font-semibold text-[var(--color-foreground)]">{continueCourse.course.title}</h2>
              <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
                {continueCourse.nextLesson
                  ? `${continueCourse.nextLesson.moduleTitle} - ${continueCourse.nextLesson.title}`
                  : continueCourse.course.description ?? "Open the course catalog to review your completed lessons."}
              </p>
              <p className="mt-3 text-sm font-medium text-[var(--color-foreground)]">
                {continueCourse.progressSummary.progressPercent}% complete - {formatCourseProgress(continueCourse)}
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              {continueCourse.nextLesson ? (
                <Link
                  to={`/portal/courses/${encodeURIComponent(continueCourse.course.slug)}/lessons/${encodeURIComponent(continueCourse.nextLesson.id)}`}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-2.5 text-sm font-medium text-white hover:opacity-90"
                >
                  <PlayCircle className="h-4 w-4" />
                  Open next lesson
                </Link>
              ) : null}
              <Link
                to={`/portal/courses/${encodeURIComponent(continueCourse.course.slug)}`}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--color-border)] px-4 py-2.5 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
              >
                <BookOpen className="h-4 w-4" />
                View course
              </Link>
            </div>
          </div>
        </section>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Loyalty and credits</h2>
          </div>
          {loyalty.error ? (
            <p className="mt-3 text-sm text-[var(--color-error)]">{loyalty.error}</p>
          ) : (
            <>
              <div className="mt-4 flex flex-wrap items-end justify-between gap-4 rounded-2xl bg-[var(--color-surface)] p-4">
                <div>
                  <p className="text-sm text-[var(--color-muted-foreground)]">Current tier</p>
                  <p className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">{loyalty.data?.tier.label ?? "Starter"}</p>
                  {loyalty.data?.tier.nextTierLabel ? (
                    <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                      {loyalty.data.tier.bookingsToNextTier} more bookings to reach {loyalty.data.tier.nextTierLabel}.
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">You are at the highest active tier.</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-[var(--color-muted-foreground)]">Member since</p>
                  <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">
                    {loyalty.data?.memberSince ? new Date(loyalty.data.memberSince).toLocaleDateString() : "Just getting started"}
                  </p>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {(loyalty.data?.accounts ?? []).length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[var(--color-border)] p-4 text-sm text-[var(--color-muted-foreground)]">
                    No active credit accounts yet. Purchases and membership balances will appear here.
                  </div>
                ) : (
                  loyalty.data?.accounts.map((account) => (
                    <div key={account.id} className="rounded-2xl border border-[var(--color-border)] p-4">
                      <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
                        {account.accountType.replaceAll("_", " ")}
                      </p>
                      <p className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{account.balance.toFixed(2)}</p>
                      <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{account.currency}</p>
                      {account.expiresAt ? (
                        <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
                          Expires {new Date(account.expiresAt).toLocaleDateString()}
                        </p>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </section>

        <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[var(--color-ai)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Recommended next steps</h2>
          </div>
          <div className="mt-4 space-y-3">
            {recommendations.isLoading ? (
              <div className="space-y-3">
                {[1, 2].map((item) => <div key={item} className="h-20 rounded-2xl bg-[var(--color-surface)] animate-pulse" />)}
              </div>
            ) : (recommendations.data?.recommendations ?? []).length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[var(--color-border)] p-4 text-sm text-[var(--color-muted-foreground)]">
                No follow-up recommendations right now.
              </div>
            ) : (
              recommendations.data?.recommendations.slice(0, 3).map((recommendation) => (
                <div key={recommendation.title} className="rounded-2xl border border-[var(--color-border)] p-4">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-[var(--color-warning)]" />
                    <p className="font-medium text-[var(--color-foreground)]">{recommendation.title}</p>
                  </div>
                  <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{recommendation.description}</p>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Upcoming bookings</h2>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Cancel or reschedule directly from your live booking schedule.</p>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          {journey.isLoading ? (
            [1, 2].map((item) => <div key={item} className="h-28 rounded-2xl bg-[var(--color-surface)] animate-pulse" />)
          ) : upcoming.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[var(--color-border)] p-10 text-center">
              <Calendar className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]" />
              <p className="mt-3 font-medium text-[var(--color-foreground)]">No upcoming visits</p>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Your next appointment will appear here as soon as it is booked.</p>
            </div>
          ) : (
            upcoming.map((booking) => (
              <div key={booking.id} className="rounded-2xl border border-[var(--color-border)] p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-[var(--color-foreground)]">{booking.serviceName}</h3>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[booking.status] ?? ""}`}>
                        {booking.status.replaceAll("_", " ")}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-[var(--color-muted-foreground)]">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="h-4 w-4" />
                        {new Date(booking.details.startAt).toLocaleDateString()}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-4 w-4" />
                        {new Date(booking.details.startAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="h-4 w-4" />
                        {formatLocation(booking.details.locationMode)}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={`/crm/bookings/${booking.id}/ics`}
                      download={`booking-${booking.id}.ics`}
                      className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                    >
                      <Calendar className="h-4 w-4" />
                      Add to calendar
                    </a>
                    <button
                      type="button"
                      onClick={() => void openReschedule(booking)}
                      disabled={busyBookingId === booking.id}
                      className="rounded-xl border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-60"
                    >
                      {busyBookingId === booking.id && rescheduleBooking?.id === booking.id ? "Loading..." : "Reschedule"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleCancelBooking(booking.id)}
                      disabled={busyBookingId === booking.id}
                      className="inline-flex items-center gap-2 rounded-xl border border-rose-200 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                    >
                      {busyBookingId === booking.id && rescheduleBooking?.id !== booking.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Journey timeline</h2>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">A chronological view of completed visits, active programs, and upcoming milestones.</p>
          </div>
          <Link to="/portal/history" className="text-sm font-medium text-[var(--color-primary)] hover:opacity-80">
            Open full history
          </Link>
        </div>

        <div className="mt-6 space-y-4">
          {steps.length === 0 && !journey.isLoading ? (
            <div className="rounded-2xl border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-muted-foreground)]">
              Your timeline will fill in as visits and enrollments are completed.
            </div>
          ) : (
            steps.map((step, index) => {
              const colorClass = TIMELINE_COLORS[hashString(`${step.serviceName}:${step.id}`) % TIMELINE_COLORS.length];
              return (
                <div key={step.id} className="relative pl-8">
                  {index < steps.length - 1 ? (
                    <div className="absolute left-[11px] top-7 h-[calc(100%+0.75rem)] w-px bg-[var(--color-border)]" />
                  ) : null}
                  <div className={`absolute left-0 top-1.5 h-6 w-6 rounded-full ${colorClass} ring-4 ring-[var(--color-background)]`} />
                  <div className="rounded-2xl border border-[var(--color-border)] p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium text-[var(--color-foreground)]">{step.serviceName}</p>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[step.status] ?? ""}`}>
                            {step.status.replaceAll("_", " ")}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                          {step.type === "booking" ? "Appointment" : "Enrollment"} on {new Date(step.date).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        <Link to="/portal/history" className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 hover:shadow-sm transition-shadow">
          <Clock className="h-6 w-6 text-[var(--color-primary)]" />
          <div className="mt-3 font-medium text-[var(--color-foreground)]">Visit history</div>
          <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">Review completed appointments and long-term progress.</div>
        </Link>
        <Link to="/portal/messages" className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 hover:shadow-sm transition-shadow">
          <MessageSquare className="h-6 w-6 text-[var(--color-success)]" />
          <div className="mt-3 font-medium text-[var(--color-foreground)]">Messages</div>
          <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">Reach your provider and keep follow-up details in one place.</div>
        </Link>
      </div>

      {rescheduleBooking ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 py-8">
          <div className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Reschedule</p>
                <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{rescheduleBooking.serviceName}</h2>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                  Current time: {new Date(rescheduleBooking.details.startAt).toLocaleString()}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setRescheduleBooking(null);
                  setAvailability(null);
                  setAvailabilityError(null);
                }}
                className="rounded-full p-2 hover:bg-[var(--color-surface)]"
              >
                <XCircle className="h-5 w-5 text-[var(--color-muted-foreground)]" />
              </button>
            </div>

            {availabilityLoading ? (
              <div className="mt-6 flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading available times...
              </div>
            ) : availabilityError ? (
              <div className="mt-6 rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-4 text-sm text-[var(--color-error)]">
                {availabilityError}
              </div>
            ) : (
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {(availability?.slots ?? []).slice(0, 10).map((slot) => (
                  <button
                    key={`${slot.startTime}:${slot.resourceId}`}
                    type="button"
                    onClick={() => void handleReschedule(slot)}
                    disabled={busyBookingId === rescheduleBooking.id}
                    className="rounded-2xl border border-[var(--color-border)] p-4 text-left hover:bg-[var(--color-surface)] disabled:opacity-60"
                  >
                    <p className="font-medium text-[var(--color-foreground)]">
                      {new Date(slot.startTime).toLocaleDateString()} at {new Date(slot.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                    <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{slot.resourceName}</p>
                  </button>
                ))}
                {!availabilityLoading && (availability?.slots ?? []).length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[var(--color-border)] p-6 text-sm text-[var(--color-muted-foreground)] sm:col-span-2">
                    No alternate slots are open right now.
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
